import React, { lazy } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Container from '@mui/material/Container';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';

// import { ProtectedRoute } from 'components/App/ProtectedRoute/ProtectedRoute';
import { getLoggedIn, getMe } from 'redux/account/selectors';

import AppDrawer from './AppDrawer';
import logo from './logo-64.jpg';

// Pages
const LoginPage = lazy(() => import('components/App/LoginPage/LoginPage'));
const ForgotPasswordPage = lazy(
  () => import('components/App/ForgotPasswordPage/ForgotPasswordPage')
);
const ResetPasswordPage = lazy(
  () => import('components/App/ResetPasswordPage/ResetPasswordPage')
);
const HomePage = lazy(() => import('components/App/HomePage/HomePage'));
const MePage = lazy(() => import('components/App/MePage/MePage'));
const MeDetailsPage = lazy(
  () => import('components/App/MeDetailsPage/MeDetailsPage')
);
const MeTxnsPage = lazy(() => import('components/App/MeTxnsPage/MeTxnsPage'));
const MeMembershipsPage = lazy(
  () => import('components/App/MeMembershipsPage/MeMembershipsPage')
);
const ItemsPage = lazy(() => import('components/App/ItemsPage/ItemsPage'));
const ItemDetailsPage = lazy(
  () => import('components/App/ItemDetailsPage/ItemDetailsPage')
);
const CartPage = lazy(() => import('components/App/CartPage/CartPage'));
const CheckoutPage = lazy(
  () => import('components/App/CheckoutPage/CheckoutPage')
);

interface IPage {
  component: JSX.Element;
  strict?: boolean;
  exact: boolean;
  path: string;
  documentTitle?: string;
  getRedirectRoute: IGetRedirectRoute;
  showTitle: boolean;
  noPadding?: boolean;
}

// Redirection methods
interface IGetRedirectRouteOptions {
  isLoggedIn?: boolean;
}

type IGetRedirectRoute = (
  options: IGetRedirectRouteOptions
) => string | undefined;

/**
 * Returns home page route if user is logged in, undefined otherwise
 * @param {boolean} options.isLoggedIn User's log in status
 * @returns {string | undefined} Redirect route
 */
const logInRedirection: IGetRedirectRoute = ({ isLoggedIn }) => {
  return isLoggedIn ? '/' : undefined;
};

/**
 * Returns login page route if user is not logged in, undefined otherwise
 * @param {boolean} options.isLoggedIn User's log in status
 * @returns {string |  undefined} Redirect route
 */
const sessionRedirection: IGetRedirectRoute = ({ isLoggedIn }) => {
  return isLoggedIn ? undefined : '/login';
};

/**
 * Noop redirection
 * @returns {undefined} Redirect route
 */
const noopRedirection: IGetRedirectRoute = () => {
  return undefined;
};

const pageList: IPage[] = [
  {
    component: <LoginPage />,
    exact: true,
    path: '/login',
    documentTitle: 'Login',
    getRedirectRoute: logInRedirection,
    showTitle: true,
  },
  {
    component: <ForgotPasswordPage />,
    exact: true,
    path: '/password/forgot',
    documentTitle: 'Forgot Password',
    getRedirectRoute: logInRedirection,
    showTitle: true,
  },
  {
    component: <ResetPasswordPage />,
    exact: true,
    path: '/password/reset/:code',
    documentTitle: 'Reset Password',
    getRedirectRoute: noopRedirection,
    showTitle: true,
  },
  {
    component: <HomePage />,
    exact: true,
    path: '/',
    documentTitle: 'Home',
    getRedirectRoute: noopRedirection,
    showTitle: false,
    noPadding: true,
  },
  {
    component: <MePage />,
    exact: true,
    path: '/me',
    documentTitle: 'Me',
    getRedirectRoute: sessionRedirection,
    showTitle: false,
  },
  {
    component: <MeDetailsPage />,
    exact: true,
    path: '/me/details',
    documentTitle: 'My Details',
    getRedirectRoute: sessionRedirection,
    showTitle: false,
  },
  {
    component: <MeTxnsPage />,
    exact: true,
    path: '/me/txns',
    documentTitle: 'My Transactions',
    getRedirectRoute: sessionRedirection,
    showTitle: false,
  },
  {
    component: <MeMembershipsPage />,
    exact: true,
    path: '/me/memberships',
    documentTitle: 'My Memberships',
    getRedirectRoute: sessionRedirection,
    showTitle: false,
  },
  {
    component: <ItemsPage />,
    exact: true,
    path: '/swap',
    documentTitle: 'Swap Time',
    getRedirectRoute: noopRedirection,
    showTitle: false,
    noPadding: true,
  },
  {
    component: <ItemDetailsPage />,
    exact: true,
    path: '/item',
    documentTitle: 'Item Details',
    getRedirectRoute: noopRedirection,
    showTitle: false,
    noPadding: true,
  },
  {
    component: <CartPage />,
    exact: true,
    path: '/cart',
    documentTitle: 'My Swap Bag',
    getRedirectRoute: sessionRedirection,
    showTitle: false,
  },
  {
    component: <CheckoutPage />,
    exact: true,
    path: '/checkout',
    documentTitle: 'Checkout',
    getRedirectRoute: sessionRedirection,
    showTitle: false,
  },
];

interface IRedirectRouteProps {
  path: string;
  redirectTo?: string;
  children?: React.ReactNode;
}

const RedirectRoute: React.FC<IRedirectRouteProps> = ({
  path,
  redirectTo,
  children,
}: IRedirectRouteProps) =>
  redirectTo ? (
    <Navigate to={redirectTo} state={{ from: path }} />
  ) : (
    <>{children}</>
  );

const AppRouter: React.FC = () => {
  const navigate = useNavigate();
  const isLoggedIn = useSelector(getLoggedIn);
  const me = useSelector(getMe);

  return (
    <Routes>
      {pageList.map(
        ({ getRedirectRoute, component, path, ...otherProps }: IPage) => {
          document.title = otherProps.documentTitle
            ? `TFP - ${otherProps.documentTitle}`
            : 'TFP';

          return (
            <Route
              {...otherProps}
              path={path}
              key="protected-route"
              element={
                <RedirectRoute
                  path={path}
                  redirectTo={getRedirectRoute({ isLoggedIn })}
                >
                  <AppBar position="fixed">
                    <Toolbar>
                      <Link
                        href="https://thefashionpulpit.com"
                        sx={{ lineHeight: 1 }}
                        underline="none"
                        color="white"
                      >
                        <img
                          src={logo}
                          srcSet={logo}
                          alt={`Logo`}
                          loading="lazy"
                        />
                      </Link>
                      <Box sx={{ flexGrow: 1 }} />
                      {isLoggedIn && (
                        <Link
                          href="#"
                          underline="none"
                          sx={{ pl: 1, pr: 1 }}
                          color="white"
                          onClick={(e) => {
                            e.preventDefault();

                            navigate('/me');
                          }}
                        >
                          {me.points}
                        </Link>
                      )}
                      {isLoggedIn && (
                        <IconButton
                          size="large"
                          aria-label="cart"
                          aria-controls="menu-cart"
                          onClick={(e) => {
                            e.preventDefault();

                            navigate('/cart');
                          }}
                          color="inherit"
                        >
                          <ShoppingBagOutlinedIcon />
                        </IconButton>
                      )}
                      <AppDrawer />
                    </Toolbar>
                  </AppBar>
                  {otherProps.showTitle && (
                    <Box sx={{ textAlign: 'center', mt: 6 }}>
                      <Typography
                        variant="h5"
                        color="primary"
                        sx={{ textAlign: 'center', pt: 6 }}
                      >
                        {otherProps.documentTitle?.toUpperCase()}
                      </Typography>
                    </Box>
                  )}
                  <Container
                    component="main"
                    sx={{ pt: 8 + (otherProps.noPadding ? 0 : 4) }}
                    maxWidth={'xl'}
                    disableGutters
                  >
                    {component}
                  </Container>
                </RedirectRoute>
              }
            />
          );
        }
      )}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRouter;
